import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { getMonth, getYear, getDayOfYear, startOfYear } from 'date-fns';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Mapeo de categorías de la app a columnas del template
const CATEGORY_TO_COLUMNS = {
  'FOOD': { amount: 1, reference: 2 },
  'GO OUT': { amount: 3, reference: 4 },
  'BILLS': { amount: 5, reference: 6 },
  'AMANDA': { amount: 7, reference: 8 },
  'MARTIN': { amount: 9, reference: 10 },
  'HOLIDAYS': { amount: 11, reference: 12 },
  'HOUSE': { amount: 13, reference: 14 }
};

// Colores según fuente del dinero (formato hex sin FF)
const SOURCE_COLORS = {
  'MARTIN BANK': 'cce5ff',    // Azul claro
  'AMANDA BANK': 'ffd6e7',    // Rosa claro  
  'MIXED': 'ffeac0'           // Naranja claro
};

export const useExcelExport = () => {
  const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Mapeo de categorías a columnas del Excel
  const CATEGORY_TO_COLUMNS = {
    'FOOD': { amount: 1, reference: 2 },      // B y C
    'GO OUT': { amount: 3, reference: 4 },    // D y E
    'BILLS': { amount: 5, reference: 6 },     // F y G
    'AMANDA': { amount: 7, reference: 8 },    // H y I
    'MARTIN': { amount: 9, reference: 10 },   // J y K
    'HOLIDAYS': { amount: 11, reference: 12 }, // L y M
    'HOUSE': { amount: 13, reference: 14 }    // N y O
  };

  // Colores por fuente de dinero
  const SOURCE_COLORS = {
    'MARTIN BANK': '87CEEB',  // Azul claro
    'AMANDA BANK': 'FFB6C1',  // Rosa claro  
    'MIXED': 'FFA500'         // Naranja
  };
  
  // Función para formatear moneda británica
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Función para obtener día del año
  const getDayOfYear = (date) => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  // Función para crear template básico cuando no se puede cargar archivo
  const createBasicTemplate = () => {
    console.log('🔧 Creando template básico...');
    const workbook = XLSX.utils.book_new();
    
    // Crear estructura básica similar al template
    const headers = [
      'Day', 'Food', 'Food Reference', 'Go Out', 'Go Out Reference', 
      'Bills', 'Bills References', 'Amanda', 'Amanda Reference', 
      'Martin', 'Martin Reference', 'Holidays', 'Holidays Reference',
      'House', 'House Reference'
    ];
    
    const data = [headers];
    
    // Agregar filas para cada día del mes (1-31)
    for (let day = 1; day <= 31; day++) {
      const row = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''];
      data.push(row);
    }
    
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, '2025');
    
    return workbook;
  };

  // Función para cargar el template como base
  const loadTemplate = () => {
    console.log('⚠️ NOTA: Los archivos Excel locales no se pueden cargar directamente desde el navegador por razones de seguridad.');
    console.log('🔧 Creando template básico en su lugar...');
    
    // Por ahora, crear un template básico ya que no podemos cargar archivos locales
    return createBasicTemplate();
  };

  // Función para convertir fecha a número de serie de Excel
  const dateToExcelSerial = (date) => {
    const epoch = new Date(1899, 11, 30); // 30 de diciembre de 1899
    const diffTime = date.getTime() - epoch.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Función para determinar el color según las fuentes del día
  const getDaySourceColor = (transactions) => {
    const sources = [...new Set(transactions.map(t => t.moneySource))];
    if (sources.length === 1) {
      return SOURCE_COLORS[sources[0]] || 'FFFFFF';
    }
    return SOURCE_COLORS.MIXED;
  };

  // Función para agrupar transacciones por año
  const groupTransactionsByYear = (transactions) => {
    const grouped = {};
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const year = getYear(date);
      
      if (!grouped[year]) {
        grouped[year] = [];
      }
      
      grouped[year].push(transaction);
    });
    
    return grouped;
  };

  // Función para procesar transacciones y crear estructura por día del año
  const processTransactionsForTemplate = (yearTransactions) => {
    // Agrupar transacciones por día del año
    const dayData = {};
    
    yearTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const dayOfYear = date.getDate(); // Usar día del mes en lugar de día del año
      
      if (!dayData[dayOfYear]) {
        dayData[dayOfYear] = {
          date: date,
          categories: {},
          allTransactions: []
        };
      }
      
      // Agrupar por categoría
      const category = transaction.expenseType;
      if (!dayData[dayOfYear].categories[category]) {
        dayData[dayOfYear].categories[category] = {
          amount: 0,
          references: [],
          transactions: []
        };
      }
      
      dayData[dayOfYear].categories[category].amount += transaction.gbpAmount;
      dayData[dayOfYear].categories[category].references.push(
        `${transaction.merchantName} (£${transaction.gbpAmount.toFixed(2).replace('.', ',')})`
      );
      dayData[dayOfYear].categories[category].transactions.push(transaction);
      dayData[dayOfYear].allTransactions.push(transaction);
    });
    
    return dayData;
  };

  // Función para actualizar el template con los datos
  const updateTemplateWithData = (templateWorkbook, yearTransactions, year, month = null) => {
    const worksheet = templateWorkbook.Sheets['2025'];
    if (!worksheet) {
      console.error('❌ No se encontró la pestaña 2025 en el template');
      return null;
    }

    // Procesar las transacciones
    const dayData = processTransactionsForTemplate(yearTransactions);
    
    // Obtener el rango del template
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    // Determinar el mes y año para generar todas las fechas
    const targetMonth = month !== null ? month : (yearTransactions.length > 0 ? new Date(yearTransactions[0].date).getMonth() : new Date().getMonth());
    const targetYear = year || new Date().getFullYear();
    
    // Obtener el número de días del mes
    const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    
    // Actualizar TODOS los días del mes (1 hasta daysInMonth)
    for (let day = 1; day <= daysInMonth; day++) {
      const rowIndex = 1 + day; // Las filas de datos empiezan en la fila 2 (índice 1)
      
      if (rowIndex <= range.e.r) {
        // Crear fecha para este día
        const dayDate = new Date(targetYear, targetMonth, day);
        const formattedDate = `${day.toString().padStart(2, '0')}/${(targetMonth + 1).toString().padStart(2, '0')}/${targetYear}`;
        
        // Actualizar columna Day (A) con formato de fecha completa
        const dayCell = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
        if (!worksheet[dayCell]) worksheet[dayCell] = {};
        worksheet[dayCell].v = formattedDate;
        worksheet[dayCell].t = 's';
        
        // Verificar si este día tiene transacciones
        const dayInfo = dayData[day];
        
        if (dayInfo && dayInfo.allTransactions.length > 0) {
          // Día con transacciones - aplicar color según fuentes
          const dayColor = getDaySourceColor(dayInfo.allTransactions);
          
          // Aplicar color de fondo a todas las celdas del día (columnas A-O)
          for (let col = 0; col < 15; col++) {
            const cell = XLSX.utils.encode_cell({ r: rowIndex, c: col });
            if (!worksheet[cell]) worksheet[cell] = {};
            if (!worksheet[cell].s) worksheet[cell].s = {};
            worksheet[cell].s.fill = { fgColor: { rgb: dayColor } };
          }
          
          // Procesar cada categoría
          Object.keys(dayInfo.categories).forEach(category => {
            const categoryData = dayInfo.categories[category];
            const columns = CATEGORY_TO_COLUMNS[category];
            
            if (columns) {
              // Actualizar columna de monto
              const amountCell = XLSX.utils.encode_cell({ r: rowIndex, c: columns.amount });
              if (!worksheet[amountCell]) worksheet[amountCell] = {};
              worksheet[amountCell].v = categoryData.amount.toFixed(2).replace('.', ',');
              worksheet[amountCell].t = 's';
              
              // Actualizar columna de referencia
              const referenceCell = XLSX.utils.encode_cell({ r: rowIndex, c: columns.reference });
              if (!worksheet[referenceCell]) worksheet[referenceCell] = {};
              worksheet[referenceCell].v = categoryData.references.join(' + ');
              worksheet[referenceCell].t = 's';
            }
          });
        } else {
          // Día sin transacciones - usar color blanco
          for (let col = 0; col < 15; col++) {
            const cell = XLSX.utils.encode_cell({ r: rowIndex, c: col });
            if (!worksheet[cell]) worksheet[cell] = {};
            if (!worksheet[cell].s) worksheet[cell].s = {};
            worksheet[cell].s.fill = { fgColor: { rgb: 'FFFFFF' } };
          }
        }
      }
    }
    
    return worksheet;
  };

  // Función principal manual para exportar (no se usa automáticamente)
  const saveToExcel = useCallback((transactions) => {
    console.log('⚠️ saveToExcel es solo para funciones internas. Usa exportMonth en su lugar.');
    return;
  }, []);

  // Función para exportar un año específico
  const exportYear = useCallback((transactions, year) => {
    const yearTransactions = transactions.filter(t => getYear(new Date(t.date)) === year);
    
    if (yearTransactions.length === 0) {
      alert('No hay transacciones para este año');
      return;
    }

    console.log(`📊 Exportación manual iniciada para el año ${year} con ${yearTransactions.length} transacciones`);

    const template = loadTemplate();
    console.log('✅ Template básico creado para exportación manual');
    
    // Crear archivo para el año completo
    const workbookCopy = XLSX.utils.book_new();
    const templateSheet = template.Sheets['2025'];
    
    if (!templateSheet) {
      alert('❌ El template no contiene la pestaña "2025" requerida.');
      return;
    }
    
    const newSheet = XLSX.utils.json_to_sheet([]);
    Object.keys(templateSheet).forEach(cellRef => {
      if (cellRef !== '!ref' && cellRef !== '!margins') {
        newSheet[cellRef] = { ...templateSheet[cellRef] };
      }
    });
    newSheet['!ref'] = templateSheet['!ref'];
    
    const updatedSheet = updateTemplateWithData({ Sheets: { '2025': newSheet } }, yearTransactions, year);
    
    if (updatedSheet) {
      XLSX.utils.book_append_sheet(workbookCopy, updatedSheet, year.toString());
      
      const fileName = `sheets/${year}/Saver-${year}.xlsx`;
      try {
        XLSX.writeFile(workbookCopy, fileName);
        console.log(`✅ Archivo exportado manualmente: ${fileName}`);
        alert(`✅ Archivo exportado exitosamente en: ${fileName}`);
      } catch (error) {
        console.error(`❌ Error al exportar ${fileName}:`, error.message);
        console.warn(`💡 Intenta crear manualmente la carpeta: sheets/${year}/`);
        alert(`❌ Error al exportar el archivo: ${error.message}\n\n💡 Asegúrate de que exista la carpeta: sheets/${year}/`);
      }
    }
  }, []);

  // Función para exportar un mes específico
  const exportMonth = useCallback((transactions, year, month) => {
    console.log(`📊 Iniciando exportación de ${MONTH_NAMES[month]} ${year}`);
    
    const template = loadTemplate();
    console.log('✅ Template básico creado para exportación de mes');

    // Filtrar transacciones del mes específico
    const monthTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      return getYear(date) === year && getMonth(date) === month;
    });

    console.log(`📊 Transacciones encontradas para ${MONTH_NAMES[month]} ${year}:`, monthTransactions.length);

    if (monthTransactions.length === 0) {
      alert(`No hay transacciones para ${MONTH_NAMES[month]} ${year}`);
      return;
    }

    // Crear una copia del template
    const workbookCopy = XLSX.utils.book_new();
    const templateSheet = template.Sheets['2025'];
    
    if (!templateSheet) {
      alert('❌ El template no contiene la pestaña "2025" requerida.');
      return;
    }
    
    const newSheet = XLSX.utils.json_to_sheet([]);
    Object.keys(templateSheet).forEach(cellRef => {
      if (cellRef !== '!ref' && cellRef !== '!margins') {
        newSheet[cellRef] = { ...templateSheet[cellRef] };
      }
    });
    newSheet['!ref'] = templateSheet['!ref'];
    
    const updatedSheet = updateTemplateWithData({ Sheets: { '2025': newSheet } }, monthTransactions, year, month);
    
    if (updatedSheet) {
      XLSX.utils.book_append_sheet(workbookCopy, updatedSheet, year.toString());
      
      const monthFileName = `sheets/${year}/${String(month + 1).padStart(2, '0')}.xlsx`;
      try {
        XLSX.writeFile(workbookCopy, monthFileName);
        console.log(`✅ Archivo de mes exportado: ${monthFileName}`);
        alert(`✅ Archivo de ${MONTH_NAMES[month]} ${year} exportado exitosamente en:\n${monthFileName}`);
      } catch (error) {
        console.error(`❌ Error al exportar ${monthFileName}:`, error.message);
        console.warn(`💡 Intenta crear manualmente la carpeta: sheets/${year}/`);
        alert(`❌ Error al exportar el archivo: ${error.message}\n\n💡 Asegúrate de que exista la carpeta: sheets/${year}/`);
      }
    }
  }, []);

  // Función para obtener resumen por año
  const getYearSummary = useCallback((transactions) => {
    const groupedByYear = groupTransactionsByYear(transactions);
    
    return Object.keys(groupedByYear).map(year => ({
      year: parseInt(year),
      transactionCount: groupedByYear[year].length,
      totalAmount: groupedByYear[year].reduce((sum, t) => sum + t.gbpAmount, 0)
    }));
  }, []);

  return {
    saveToExcel,
    exportYear,
    exportMonth,
    getYearSummary,
    formatCurrency,
    groupTransactionsByYear
  };
}; 